import { request } from "../utils/request";

export abstract class APIManager {
  abstract baseURL: string;
  abstract id: string;
  abstract getUpsertJSON(): string;

  private validateID(): void {
    if (this.id === "") throw new Error("ID of resource is empty.");
  }

  public async create(): Promise<any | void> {
    const url = new URL(`${this.baseURL}`);
    const options = { method: "POST", body: this.getUpsertJSON() };
    await request(url, options, true);
  }

  public async update(): Promise<void> {
    this.validateID();
    const url = new URL(`${this.baseURL}/${this.id}`);
    const options = { method: "PUT", body: this.getUpsertJSON() };
    await request(url, options, true);
  }

  public async delete(): Promise<void> {
    this.validateID();
    const url = new URL(`${this.baseURL}/${this.id}`);
    const options = { method: "DELETE" };
    await request(url, options, true);
    // Invalidate the ID, so any other update/delete will fail.
    this.id = "";
  }
}

export abstract class APIResource extends APIManager {
  abstract upsertFields: string[];

  public hasErrors = (errors: { [key: string]: string } = this.validate()) =>
    Object.values(errors).some((x) => x);

  // TODO: Create a new function to cast to HTML input types if needed.
  // See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
  public getType = (fieldName: string) => typeof this[fieldName as keyof APIResource];

  public castToType(fieldName: string, value: any): any {
    // Since most input text box values are coming as string we need to cast them to proper types.
    const type = this.getType(fieldName);
    // TODO: Handle other types if needed.
    switch (type) {
      case "string":
        return String(value);
      case "number":
        return Number(value);
      case "boolean":
        return Boolean(value);
      default:
        return value;
    }
  }

  public fieldsWithType(): { [key: string]: string } {
    const fields: { [key: string]: string } = {};
    for (const field of this.upsertFields) fields[field] = this.getType(field);
    return fields;
  }

  public validate(): { [key: string]: string } {
    const errors: { [key: string]: string } = {};
    for (const key of this.upsertFields) {
      const error = this.hasError(key);
      if (error == null) continue;
      errors[key] = error;
    }
    return errors;
  }

  public hasError(fieldName: string): string | null {
    const fieldValue: any = this[fieldName as keyof APIResource];
    const funcName = `${fieldName}__validation` as keyof APIResource;
    if (funcName in this && typeof this[funcName] === "function") {
      const validateFunc = this[funcName] as Function;
      return validateFunc.apply(this, [fieldValue]);
    }
    return null;
  }

  getUpsertJSON(): string {
    // Return only fields upsertFields contains.
    return JSON.stringify(this, this.upsertFields);
  }
}
